import { useCallback, useMemo, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { LASER_CENTERS, SMILE_CENTERS, VERTICAL_CENTERS } from "@/lib/brandConfig";
import { useTeam } from "@/lib/useTeam";
import { useCurrentProfile } from "@/lib/CurrentProfileContext";
import { useToast } from "@/lib/useToast";
import { Toast } from "@/components/ui";
import { centersWithoutSales, teamMatrixCenters } from "@/lib/managerStats";
import { ROLE_LABELS } from "./managerUi";

const KNOWN_CENTERS = [...SMILE_CENTERS, ...LASER_CENTERS, ...VERTICAL_CENTERS];
const BRAND_GROUPS: { label: string; centers: readonly string[] }[] = [
  { label: "Smile World", centers: SMILE_CENTERS },
  { label: "Laser World", centers: LASER_CENTERS },
  { label: "Vertical World", centers: VERTICAL_CENTERS },
];

const keyOf = (center: string, profileId: string) => `${center}\u0000${profileId}`;
const isManagerRole = (role: string | undefined) => role === "sales_manager";

export function ManagerTeamTab() {
  const { members, profiles, profilesById, addMember, removeMember, loading, error, refresh } = useTeam();
  const { profile: me } = useCurrentProfile();
  const { toast, showToast, hideToast } = useToast();
  /** Changements en cours d'envoi : valeur voulue (optimiste). */
  const [pending, setPending] = useState<Map<string, boolean>>(new Map());

  const centers = useMemo(
    () => teamMatrixCenters(KNOWN_CENTERS, members.map((member) => member.center)),
    [members],
  );
  const groups = useMemo(() => {
    const extras = centers.filter((center) => !KNOWN_CENTERS.includes(center));
    return [...BRAND_GROUPS, ...(extras.length ? [{ label: "Autres", centers: extras }] : [])].filter(
      (group) => group.centers.length > 0,
    );
  }, [centers]);
  const orderedCenters = useMemo(() => groups.flatMap((group) => [...group.centers]), [groups]);

  const memberSet = useMemo(() => new Set(members.map((m) => keyOf(m.center, m.profile_id))), [members]);
  const isMember = useCallback(
    (center: string, profileId: string) => {
      const key = keyOf(center, profileId);
      return pending.has(key) ? pending.get(key)! : memberSet.has(key);
    },
    [pending, memberSet],
  );

  const effectiveMembers = useMemo(
    () =>
      orderedCenters.flatMap((center) =>
        profiles.filter((p) => isMember(center, p.id)).map((p) => ({ center, profile_id: p.id })),
      ),
    [orderedCenters, profiles, isMember],
  );
  const orphanCenters = useMemo(
    () => centersWithoutSales(orderedCenters, effectiveMembers, (id) => isManagerRole(profilesById.get(id)?.role)),
    [orderedCenters, effectiveMembers, profilesById],
  );

  const sortedProfiles = useMemo(
    () =>
      [...profiles].sort(
        (a, b) =>
          Number(isManagerRole(b.role)) - Number(isManagerRole(a.role)) ||
          (a.full_name || a.email).localeCompare(b.full_name || b.email, "fr"),
      ),
    [profiles],
  );

  const toggle = async (center: string, profileId: string) => {
    const key = keyOf(center, profileId);
    if (pending.has(key)) return;
    const next = !isMember(center, profileId);
    setPending((prev) => new Map(prev).set(key, next));
    try {
      if (next) await addMember(center, profileId);
      else await removeMember(center, profileId);
    } catch (err) {
      const who = profilesById.get(profileId);
      showToast({
        type: "error",
        message: `${next ? "Ajout" : "Retrait"} impossible (${who?.full_name || who?.email || "utilisateur"} · ${center}) : ${
          err instanceof Error ? err.message : "réessayez."
        }`,
        duration: 6000,
      });
    } finally {
      setPending((prev) => {
        const copy = new Map(prev);
        copy.delete(key);
        return copy;
      });
    }
  };

  if (error && !profiles.length) {
    return (
      <div role="alert" className="flex items-center justify-between rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        {error}
        <button type="button" onClick={() => void refresh()} className="font-medium underline">
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Cochez les centres de chaque personne. Une tâche d'équipe est visible par tous les membres du centre du dossier,
        sauf par son auteur. Les modifications sont enregistrées immédiatement.
      </p>

      {!loading && orphanCenters.length > 0 && (
        <div role="status" className="rounded-md border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900">
          <span className="font-semibold">
            {orphanCenters.length === 1 ? "1 centre sans commercial" : `${orphanCenters.length} centres sans commercial`}
          </span>{" "}
          (en dehors du manager) : {orphanCenters.join(", ")}. Les tâches d'équipe de ces centres ne seraient vues par
          personne.
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-gray-100">
              <th rowSpan={2} className="sticky left-0 z-10 w-[280px] min-w-[280px] bg-white px-4 py-2 text-left align-bottom text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                Utilisateur
              </th>
              {groups.map((group) => (
                <th
                  key={group.label}
                  colSpan={group.centers.length}
                  className="border-l border-gray-100 px-2 py-1.5 text-center text-[11px] font-semibold uppercase tracking-wide text-gray-500"
                >
                  {group.label}
                </th>
              ))}
            </tr>
            <tr className="border-b border-gray-200">
              {groups.map((group) =>
                group.centers.map((center, index) => (
                  <th
                    key={center}
                    scope="col"
                    className={`h-12 min-w-[76px] px-1.5 py-1 text-center align-bottom text-xs font-medium leading-tight text-gray-700 ${index === 0 ? "border-l border-gray-100" : ""}`}
                  >
                    {center}
                  </th>
                )),
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && profiles.length === 0 ? (
              <tr>
                <td colSpan={orderedCenters.length + 1} className="px-4 py-10 text-center text-gray-400" role="status">
                  Chargement des équipes…
                </td>
              </tr>
            ) : sortedProfiles.length === 0 ? (
              <tr>
                <td colSpan={orderedCenters.length + 1} className="px-4 py-10 text-center text-gray-500">
                  Aucun utilisateur interne.
                </td>
              </tr>
            ) : (
              sortedProfiles.map((profile) => {
                const name = profile.full_name?.trim() || profile.email;
                return (
                  <tr key={profile.id} className="hover:bg-gray-50/60">
                    <th scope="row" className="sticky left-0 z-10 bg-white px-4 py-2 text-left font-normal">
                      <div className="flex items-center gap-2">
                        <span className="truncate font-medium text-gray-900">{name}</span>
                        {profile.id === me?.id && <span className="text-xs text-gray-400">(vous)</span>}
                        <span
                          className={`ml-auto shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium ${
                            isManagerRole(profile.role) ? "bg-indigo-50 text-indigo-700" : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {ROLE_LABELS[profile.role] ?? profile.role}
                        </span>
                      </div>
                      <div className="truncate text-xs text-gray-500">{profile.email}</div>
                    </th>
                    {groups.map((group) =>
                      group.centers.map((center, index) => {
                        const checked = isMember(center, profile.id);
                        const busy = pending.has(keyOf(center, profile.id));
                        return (
                          <td
                            key={center}
                            className={`px-1.5 py-2 text-center ${index === 0 ? "border-l border-gray-100" : ""} ${checked ? "bg-indigo-50/40" : ""}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={busy}
                              onChange={() => void toggle(center, profile.id)}
                              aria-label={`${name} membre de l'équipe ${center}`}
                              className="h-4 w-4 cursor-pointer rounded border-gray-300 accent-indigo-600 text-indigo-600 focus:ring-2 focus:ring-indigo-500 disabled:cursor-wait disabled:opacity-50"
                            />
                          </td>
                        );
                      }),
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
          {sortedProfiles.length > 0 && (
            <tfoot className="border-t border-gray-200 bg-gray-50/70">
              <tr>
                <th scope="row" className="sticky left-0 z-10 bg-gray-50 px-4 py-2 text-left text-xs font-semibold text-gray-600">
                  Membres par centre
                </th>
                {groups.map((group) =>
                  group.centers.map((center, index) => {
                    const count = effectiveMembers.filter((m) => m.center === center).length;
                    const orphan = orphanCenters.includes(center);
                    return (
                      <td
                        key={center}
                        className={`px-1.5 py-2 text-center text-xs tabular-nums ${index === 0 ? "border-l border-gray-100" : ""} ${
                          orphan ? "font-semibold text-amber-700" : "font-medium text-gray-700"
                        }`}
                        title={orphan ? "Aucun commercial : les tâches d'équipe ne seraient vues par personne" : undefined}
                      >
                        <span className="inline-flex items-center gap-1">
                          {count}
                          {orphan && <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />}
                          {orphan && <span className="sr-only">aucun commercial</span>}
                        </span>
                      </td>
                    );
                  }),
                )}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} duration={toast.duration} />
    </div>
  );
}
